import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import Icon from "../icon/icon";
import { useRouter } from "hooks";
import { mixpanel, TrackEvent } from "mixpanel";
import { setGlobalDateTimeFilter } from "services";
import { removeWhitespace } from "services/number.service";
import lang from "translations";
import styles from "./filter-bar.module.scss";

export const FilterBarContext = createContext(null);

const isUid = (value = "") => {
  const uidPattern = /([0-9A-Z]{4}\s+){3}([0-9A-Z]{2})/;
  return uidPattern.test(value);
};

const FilterBar = ({
  children,
  className,
  filterState = {},
  loading = false,
  maxLength = 75,
  onApply,
  onClear,
  placeholder,
  searchBox = true,
  showClear = true,
  syncStateOnApply = true,
  trackActions = true,
}) => {
  const rootRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchKey, setSearchKey] = useState(filterState.searchKey || "");
  const [submitting, setSubmitting] = useState(false);
  const { history, pathname } = useRouter();

  const closeFilter = useCallback(() => setActiveFilter(null), []);

  const toggleFilter = useCallback((filterId) => {
    setActiveFilter((current) => (current === filterId ? null : filterId));
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        closeFilter();
      }
    };

    document.addEventListener("pointerdown", handleDocumentClick, true);
    return () => document.removeEventListener("pointerdown", handleDocumentClick, true);
  }, [closeFilter]);

  const applySearch = useCallback(
    async (searchKeyOverride) => {
      const nextSearchKey = searchKeyOverride ?? searchKey;
      await onApply(nextSearchKey);
    },
    [onApply, searchKey],
  );

  const applyFilters = useCallback(async () => {
    if (loading || submitting) return;

    setSubmitting(true);
    try {
      if (trackActions) {
        mixpanel.track(TrackEvent.ClickedButton, {
          button: lang.applyFilter,
        });
      }

      await applySearch();
      if (syncStateOnApply) {
        const locationFilterState = { ...filterState };
        delete locationFilterState.dateRange;
        delete locationFilterState.venueId;
        history.replace({ pathname, state: locationFilterState });

        if (filterState.dateRange) {
          setGlobalDateTimeFilter(filterState.dateRange);
        }
      }
      closeFilter();
    } finally {
      setSubmitting(false);
    }
  }, [
    applySearch,
    closeFilter,
    filterState,
    history,
    loading,
    pathname,
    submitting,
    syncStateOnApply,
    trackActions,
  ]);

  const clearFilters = useCallback(() => {
    if (trackActions) {
      mixpanel.track(TrackEvent.ClickedButton, {
        button: lang.clearAllFilters,
      });
    }
    setSearchKey("");
    closeFilter();
    onClear();
  }, [closeFilter, onClear, trackActions]);

  const contextValue = {
    activeFilter,
    closeFilter,
    toggleFilter,
  };

  const handleToolbarPointerDown = useCallback(
    (event) => {
      if (!event.target?.closest?.("[data-filter-bar-control]")) {
        closeFilter();
      }
    },
    [closeFilter],
  );

  return (
    <FilterBarContext.Provider value={contextValue}>
      <div
        ref={rootRef}
        className={classnames(styles.toolbar, className)}
        onPointerDown={handleToolbarPointerDown}
      >
        {searchBox && (
          <div className={styles.searchRow}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              maxLength={maxLength}
              placeholder={placeholder}
              aria-label={placeholder}
              onChange={(event) => setSearchKey(event.target.value)}
              value={searchKey}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  if (isUid(searchKey)) {
                    const normalizedSearchKey = removeWhitespace(searchKey);
                    setSearchKey(normalizedSearchKey);
                    applySearch(normalizedSearchKey);
                    return;
                  }
                  applySearch();
                }
              }}
              onBlur={() => {
                if (isUid(searchKey)) {
                  setSearchKey(removeWhitespace(searchKey));
                }
              }}
              onFocus={closeFilter}
            />
          </div>
        )}

        <div className={styles.topRow}>
          {children}
        </div>
        <div className={styles.actionsRow}>
          <div className={styles.rowActions}>
            {showClear && (
              <button
                type="button"
                className={styles.clearButton}
                disabled={loading || submitting}
                onClick={clearFilters}
              >
                {lang.clearAll}
              </button>
            )}
            <button
              type="button"
              className={styles.applyFilterButton}
              disabled={loading || submitting}
              onClick={applyFilters}
            >
              {(loading || submitting) && <Icon loading paddingless className={styles.loadingIcon} />}
              {lang.filter}
            </button>
          </div>
        </div>
      </div>
    </FilterBarContext.Provider>
  );
};

export const useFilterBar = () => {
  const context = useContext(FilterBarContext);
  if (!context) {
    throw new Error("FilterBar controls must be rendered inside FilterBar");
  }
  return context;
};

export const FilterControl = ({
  align = "left",
  children,
  disabled = false,
  icon,
  id,
  label,
  loading = false,
  menuClassName,
  onOpen,
  onMenuScroll,
  summary,
}) => {
  const { activeFilter, toggleFilter } = useFilterBar();
  const open = activeFilter === id;

  return (
    <div className={styles.filterWrap} data-filter-bar-control>
      <button
        type="button"
        className={classnames(styles.filterPill, { [styles.filterPillOpen]: open })}
        disabled={disabled || loading}
        aria-expanded={open}
        onClick={() => {
          if (!open && onOpen) onOpen();
          toggleFilter(id);
        }}
      >
        {icon && <span className={styles.pillIcon}>{icon}</span>}
        <span className={styles.pillLabel}>{label}</span>
        <span className={styles.pillValue}>{loading ? lang.loading : summary}</span>
        <ChevronDownIcon className={styles.chevronIcon} />
      </button>
      {open && (
        <div
          className={classnames(
            styles.menu,
            { [styles.menuEnd]: align === "right" },
            menuClassName,
          )}
          onScroll={onMenuScroll}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const ChevronDownIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);

export default FilterBar;
